import { ForwardAttachmentChanges } from "@/components/mailThread/forwardPayload";
import Email from "@/domain/entities/emails/Email";
import EmailToSend from "@/domain/entities/emails/EmailToSend";
import {
    emailToSendToForwardMailSDKMapper,
    emailToSendToSendMailSDKMapper,
} from "@/mappers/email/emailMapper";
import { useSdk } from "@cs_teamwaydev/mailvista-sdk_react";
import { useTranslation } from "react-i18next";
import { useToast } from "./utils/useToast";

const useSendMail = (): {
    sendMail: (mail: EmailToSend) => Promise<boolean>;
    replyToMail: (mail: EmailToSend, replyTo: Email) => Promise<boolean>;
    forwardMail: (
        forwardId: string,
        mail: EmailToSend,
        forwardTo: Email,
        changes: ForwardAttachmentChanges,
        quotedBody?: string,
    ) => Promise<boolean>;
} => {
    const { mailManager } = useSdk();
    const { toast } = useToast();
    const { t } = useTranslation();

    const handleAction = async (
        action: () => Promise<Boolean>,
        errorMsg: string,
    ): Promise<boolean> => {
        try {
            const success = await action();
            if (!success) {
                throw new Error(errorMsg);
            }

            return true;
        } catch (error) {
            console.error(errorMsg, error);
            toast({
                title: t("errors.sendMail"),
                description: t("errors.global.description"),
                variant: "error",
            });

            return false;
        }
    };

    const sendMail = async (mail: EmailToSend) => {
        const success = await handleAction(
            () => mailManager.sendMail(emailToSendToSendMailSDKMapper(mail)),
            "Failed to send mail",
        );

        return success;
    };

    const replyToMail = async (mail: EmailToSend, replyTo: Email) => {
        const success = await handleAction(
            () =>
                mailManager.replyToMail(
                    replyTo.internetMessageId,
                    emailToSendToForwardMailSDKMapper(mail),
                ),
            "Failed to reply to mail",
        );

        return success;
    };

    const forwardMail = async (
        forwardId: string,
        mail: EmailToSend,
        forwardTo: Email,
        changes: ForwardAttachmentChanges,
        quotedBody?: string,
    ) => {
        const success = await handleAction(
            () =>
                mailManager.forwardMail({
                    forwardId,
                    uniqueMailvistaId: forwardTo.uniqueMailvistaId,
                    toRecipients: mail.toRecipients ?? [],
                    ccRecipients: mail.ccRecipients ?? [],
                    bccRecipients: mail.bccRecipients ?? [],
                    subject: mail.subject || undefined,
                    note: mail.content,
                    quotedBody,
                    ...changes,
                }),
            "Failed to forward mail",
        );

        return success;
    };

    return {
        sendMail,
        replyToMail,
        forwardMail,
    };
};

export default useSendMail;
